import React, { useEffect, useState } from "react";
import api from "../services/api";
import { qlAlert } from "../utils/dialogs";
import { SeancesLecture } from "./soutien/Seances";
import "./soutien/soutien.css";

const list = data => Array.isArray(data) ? data : (data?.results || []);
const message = error => error?.response?.data?.detail || Object.values(error?.response?.data || {})?.flat?.()[0] || "Une erreur est survenue.";
const STATUS = { draft: "Brouillon", pending: "En attente", approved: "Approuvé", rejected: "Refusé", suspended: "Suspendu", new: "Nouvelle", contacting: "Prise de contact", confirmed: "Confirmée", completed: "Effectuée", cancelled: "Annulée" };
// L'administration met en relation (« Confirmée ») ; les séances sont ensuite déclarées par le parent.
const STATUTS_DEMANDE_ADMIN = ["new", "contacting", "confirmed", "cancelled"];
const LIBELLE_STATUT_ADMIN = { confirmed: "Confirmée — mise en relation" };
const STATUS_CLASS = { draft: "secondary", pending: "warning", approved: "success", rejected: "danger", suspended: "danger" };
const MODALITY_LABELS = { online: "En ligne", home: "À domicile", teacher_place: "Chez l’intervenant" };
const REVIEW_STATUS = { pending: "En attente", published: "Publié", reported: "Signalé", hidden: "Masqué" };
const REVIEW_STATUS_CLASS = { pending: "warning", published: "success", reported: "danger", hidden: "secondary" };

// Note Quick Learning : le CEO attribue de 1 à 5 étoiles, affichées aux parents sous ce nom,
// à côté (et non à la place) de la moyenne des notes des parents.
function NoteCeo({ profil, estCeo, onChange }) {
  const [survol, setSurvol] = useState(0);
  const note = profil.ceo_rating || 0;
  const affichee = survol || note;
  const enregistrer = async (valeur) => {
    try {
      const r = await api.post(`parent-teacher/admin/profiles/${profil.id}/ceo_rating/`, { rating: valeur });
      onChange(r.data.ceo_rating);
    } catch (e) { qlAlert(message(e), "error"); }
  };
  return (
    <div className="ap-detail-matieres-section">
      <div className="ap-detail-section-title">NOTE QUICK LEARNING (CEO)</div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <div className="pt-star-picker mb-0" role={estCeo ? "radiogroup" : undefined} aria-label="Note Quick Learning" onMouseLeave={() => setSurvol(0)}>
          {[1, 2, 3, 4, 5].map(n => estCeo
            ? <button key={n} type="button" role="radio" aria-checked={note === n} aria-label={`${n} sur 5`} className={n <= affichee ? "is-filled" : ""}
                onMouseEnter={() => setSurvol(n)} onClick={() => enregistrer(n)}>★</button>
            : <span key={n} className={n <= note ? "is-filled" : ""} aria-hidden="true">{n <= note ? "★" : "☆"}</span>)}
        </div>
        <span className="small fw-semibold">{note ? `${note}/5` : "Aucune note"}</span>
        {estCeo && note > 0 && <button type="button" className="btn btn-sm btn-link" onClick={() => enregistrer(null)}>Retirer</button>}
      </div>
      <p className="text-muted small mb-0">{estCeo
        ? "Touchez une étoile pour noter. Les parents la voient sous le nom « Note Quick Learning », à côté de la moyenne de leurs propres notes."
        : "Seul le CEO peut attribuer cette note."}</p>
    </div>
  );
}

export default function AdminSupportSpace({ user = null }) {
  const [profiles, setProfiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reasonFor, setReasonFor] = useState(null);
  const [reasonText, setReasonText] = useState("");

  const load = () => Promise.all([
    api.get("parent-teacher/admin/profiles/"),
    api.get("parent-teacher/admin/requests/"),
    api.get("parent-teacher/admin/reviews/"),
  ]).then(([p, r, rv]) => { setProfiles(list(p.data)); setRequests(list(r.data)); setReviews(list(rv.data)); }).catch(e => setError(message(e)));
  useEffect(() => { load(); }, []);

  const moderateReview = async (id, status) => {
    try { await api.post(`parent-teacher/admin/reviews/${id}/moderate/`, { status }); qlAlert("Modération enregistrée.", "success"); load(); }
    catch (e) { qlAlert(message(e), "error"); }
  };

  const decide = async (id, status, reason = "") => {
    try { await api.post(`parent-teacher/admin/profiles/${id}/decide/`, { status, reason }); load(); }
    catch (e) { qlAlert(message(e), "error"); }
  };
  const confirmReason = async () => {
    if (!reasonText.trim()) return;
    await decide(reasonFor.id, reasonFor.status, reasonText.trim());
    setReasonFor(null); setReasonText("");
  };
  const setRequestStatus = async (id, status) => {
    try { await api.post(`parent-teacher/admin/requests/${id}/set_status/`, { status }); load(); }
    catch (e) { qlAlert(message(e), "error"); }
  };

  const requestsByTeacher = requests.reduce((acc, r) => {
    (acc[r.teacher.id] ||= []).push(r);
    return acc;
  }, {});
  const reviewsByTeacher = reviews.reduce((acc, rv) => {
    (acc[rv.teacher_profile] ||= []).push(rv);
    return acc;
  }, {});
  const selected = profiles.find(p => p.id === selectedId) || null;
  const selectedRequests = selected ? (requestsByTeacher[selected.id] || []) : [];
  const selectedReviews = selected ? (reviewsByTeacher[selected.id] || []) : [];

  return <div className={`ap-users-layout${selected ? " with-detail" : ""}`}>
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <h1 className="ap-page-title">Soutien scolaire</h1>
          <p className="ap-page-desc">Enseignants inscrits pour le soutien scolaire. Cliquez sur une carte pour voir le profil complet, l’approuver, la refuser, la suspendre, ou gérer les demandes des parents.</p>
        </div>
      </div>
      <section className="ap-card mb-3 p-3" aria-label="Séances et montants du mois">
        <h2 className="h6 fw-bold mb-1">Séances et montants du mois</h2>
        <p className="text-muted small mb-2">Déclarées par les parents après chaque cours. Montant = heures déclarées × tarif horaire de l’enseignant. Votre rôle : approuver les candidatures et mettre les parents en relation (« Confirmée »).</p>
        <SeancesLecture url="parent-teacher/admin/sessions/" colonnes={["teacher_name", "parent_name", "student_name"]} />
      </section>
      {error && <div className="alert alert-warning">{error}</div>}
      {profiles.length === 0 ? <p>Aucun enseignant inscrit pour le moment.</p> : (
        <div className="pt-directory">
          {profiles.map(p => {
            const count = (requestsByTeacher[p.id] || []).length;
            return (
              <article
                key={p.id}
                className={`pt-teacher-card ap-teacher-card${selectedId === p.id ? " is-selected" : ""}`}
                onClick={() => setSelectedId(p.id)}
              >
                <span className={`ap-teacher-card-status badge bg-${STATUS_CLASS[p.status] || "secondary"}`}>{STATUS[p.status]}</span>
                {p.photo_url ? <img src={p.photo_url} alt="" /> : <div className="pt-avatar"><i className="bi bi-person"></i></div>}
                <div>
                  <h3>{p.full_name}</h3>
                  <p>{p.independent ? "Intervenant indépendant" : p.establishment}</p>
                  <p><i className="bi bi-geo-alt"></i> {p.city}</p>
                  <div className="pt-tag-group">
                    <span className="pt-tag-label">Matières</span>
                    <div className="pt-tags pt-tags-subjects">
                      {p.subjects.map(x => <span key={x.id}>{x.name}{p.hourly_rates?.[x.id] ? ` · ${p.hourly_rates[x.id]} FCFA/h` : ""}</span>)}
                    </div>
                  </div>
                  {p.version_publiee_visible && p.status !== "approved" && <small className="d-block text-success">Modification d'une fiche déjà publiée (l'ancienne version reste en ligne)</small>}
                  {(p.status === "approved" || p.version_publiee_visible) && <small>{count} demande{count > 1 ? "s" : ""} de cours</small>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>

    {selected && (
      <aside className="ap-detail-panel">
        <div className="ap-detail-header">
          <span>Profil enseignant</span>
          <button className="ap-detail-close" onClick={() => setSelectedId(null)}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="ap-detail-user">
          {selected.photo_url
            ? <img src={selected.photo_url} alt="" className="ap-detail-avatar-img" />
            : <div className="ap-detail-avatar">{(selected.full_name || "?").slice(0, 1).toUpperCase()}</div>}
          <div>
            <div className="ap-detail-uname">{selected.full_name}</div>
            <div className="ap-detail-email">{selected.independent ? "Intervenant indépendant" : selected.establishment}</div>
            <span className={`badge bg-${STATUS_CLASS[selected.status] || "secondary"}`}>{STATUS[selected.status]}</span>
          </div>
        </div>

        <div className="ap-detail-rows">
          <div className="ap-detail-row"><span className="ap-detail-row-label">Ville</span><span className="ap-detail-row-val">{[selected.city, selected.zone].filter(Boolean).join(" · ") || "—"}</span></div>
          {selected.version_publiee_visible && selected.status !== "approved" && <div className="ap-detail-row"><span className="ap-detail-row-label">En ligne</span><span className="ap-detail-row-val">La version approuvée précédente reste visible des parents jusqu'à votre décision.</span></div>}
          <div className="ap-detail-row"><span className="ap-detail-row-label">Modalités</span><span className="ap-detail-row-val">{(selected.modalities || []).map(m => MODALITY_LABELS[m] || m).join(", ") || "—"}</span></div>
          <div className="ap-detail-row"><span className="ap-detail-row-label">Disponibilités</span><span className="ap-detail-row-val">{Object.entries(selected.availability || {}).map(([d, h]) => `${d} : ${h}`).join(", ") || "—"}</span></div>
          {selected.decision_reason && <div className="ap-detail-row"><span className="ap-detail-row-label">Motif</span><span className="ap-detail-row-val">{selected.decision_reason}</span></div>}
        </div>

        {selected.presentation && (
          <div className="ap-detail-matieres-section">
            <div className="ap-detail-section-title">PRÉSENTATION</div>
            <p>{selected.presentation}</p>
          </div>
        )}

        <div className="ap-detail-matieres-section">
          <div className="ap-detail-section-title">MATIÈRES</div>
          <div className="ap-detail-mat-grid">
            {selected.subjects.map(x => <span key={x.id} className="ap-detail-mat-chip">{x.name}{selected.hourly_rates?.[x.id] ? ` · ${selected.hourly_rates[x.id]} FCFA/h` : ""}</span>)}
          </div>
        </div>
        <div className="ap-detail-matieres-section">
          <div className="ap-detail-section-title">CLASSES</div>
          <div className="ap-detail-mat-grid">
            {selected.classes.map(x => <span key={x.id} className="ap-detail-mat-chip">{x.name}</span>)}
          </div>
        </div>

        <div className="ap-detail-actions">
          <button className="btn btn-sm btn-success" onClick={() => decide(selected.id, "approved")}>Approuver</button>
          <button className="btn btn-sm btn-danger" onClick={() => setReasonFor({ id: selected.id, status: "rejected" })}>Refuser</button>
          <button className="btn btn-sm btn-warning" onClick={() => setReasonFor({ id: selected.id, status: "suspended" })}>Suspendre</button>
          {reasonFor?.id === selected.id && (
            <form className="pt-reason-form" onSubmit={e => { e.preventDefault(); confirmReason(); }}>
              <input required autoFocus placeholder="Motif obligatoire" value={reasonText} onChange={e => setReasonText(e.target.value)} />
              <button className="btn btn-sm btn-primary">Confirmer</button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setReasonFor(null); setReasonText(""); }}>Annuler</button>
            </form>
          )}
        </div>

        <NoteCeo profil={selected} estCeo={!!user?.is_primary_admin}
          onChange={(valeur) => setProfiles(ps => ps.map(p => (p.id === selected.id ? { ...p, ceo_rating: valeur } : p)))} />

        {(selected.status === "approved" || selected.version_publiee_visible) && (
          <div className="ap-detail-matieres-section">
            <div className="ap-detail-section-title">PARENTS AYANT SOLLICITÉ CET ENSEIGNANT ({selectedRequests.length})</div>
            {selectedRequests.length === 0 ? <p className="text-muted">Aucune demande pour le moment.</p> : selectedRequests.map(r => (
              <article className="pt-parent-request" key={r.id}>
                <div>
                  <strong>{r.parent.full_name}</strong><span> — {STATUS[r.status]}</span>
                  <p>{r.parent.city} · {r.parent.neighborhood} · {r.parent.phone} · {r.parent.email}</p>
                  <p>Élève : {r.student_name || r.parent.child_name || "—"} · Classe : {r.class_name} · Matière : {r.subject_name}</p>
                  {(r.location || r.preferred_slots) && <p>{[r.location && `Lieu : ${r.location}`, r.preferred_slots && `Disponibilités : ${r.preferred_slots}`].filter(Boolean).join(" · ")}</p>}
                  {r.message && <p className="pt-parent-message">« {r.message} »</p>}
                </div>
                <select value={r.status} onChange={e => setRequestStatus(r.id, e.target.value)} aria-label="Statut de la demande">
                  {(r.status === "completed" ? [...STATUTS_DEMANDE_ADMIN, "completed"] : STATUTS_DEMANDE_ADMIN)
                    .map(x => <option key={x} value={x}>{LIBELLE_STATUT_ADMIN[x] || STATUS[x]}</option>)}
                </select>
              </article>
            ))}
          </div>
        )}

        {(selected.status === "approved" || selected.version_publiee_visible) && (
          <div className="ap-detail-matieres-section">
            <div className="ap-detail-section-title">SÉANCES DÉCLARÉES ET MONTANT DU MOIS</div>
            <SeancesLecture url="parent-teacher/admin/sessions/" filtre={`&teacher=${selected.id}`} colonnes={["parent_name", "student_name"]} />
          </div>
        )}

        {(selected.status === "approved" || selected.version_publiee_visible) && (
          <div className="ap-detail-matieres-section">
            <div className="ap-detail-section-title">AVIS DES PARENTS ({selectedReviews.length})</div>
            {selectedReviews.length === 0 ? <p className="text-muted">Aucun avis pour le moment.</p> : selectedReviews.map(rv => (
              <article className="pt-review-card" key={rv.id}>
                <div>
                  <strong>{rv.parent_name}</strong> <span className={`badge bg-${REVIEW_STATUS_CLASS[rv.status] || "secondary"}`}>{REVIEW_STATUS[rv.status] || rv.status}</span>
                  <div className="pt-star-picker" aria-hidden="true">{[1,2,3,4,5].map(n => <span key={n} className={n <= rv.rating ? "is-filled" : ""}>{n <= rv.rating ? "★" : "☆"}</span>)}</div>
                  {rv.comment && <p className="pt-parent-message">« {rv.comment} »</p>}
                  {rv.report_reason && <p className="text-muted small">Signalement : {rv.report_reason}</p>}
                </div>
                <div className="d-flex gap-2">
                  {rv.status !== "published" && <button className="btn btn-sm btn-success" onClick={() => moderateReview(rv.id, "published")}>Publier</button>}
                  {rv.status !== "hidden" && <button className="btn btn-sm btn-outline-danger" onClick={() => moderateReview(rv.id, "hidden")}>Masquer</button>}
                </div>
              </article>
            ))}
          </div>
        )}
      </aside>
    )}
  </div>;
}
